import React, { useCallback, useMemo } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import { WALK_IN_CUSTOMER } from '../../utils/saleCustomer';
import type {
  PickerContact,
  PickerCustomer,
  PickerRow,
} from '../../utils/customerPicker';
import PersonAvatar from './PersonAvatar';
import PersonRow from './PersonRow';

export interface CustomerPickerListHandlers {
  onSelectWalkIn: () => void;
  onSelectLabel: (name: string) => void;
  onSelectCustomer: (customer: PickerCustomer) => void;
  /** Saves a name known only from past orders as a customer. */
  onSaveCustomer: (customer: PickerCustomer) => void;
  onSelectContact: (contact: PickerContact) => void;
  onRetryContact: (contact: PickerContact) => void;
  onUseLabel: (name: string) => void;
  /** Opens the New customer page, with the typed name filled in when given. */
  onAddNew: (name?: string) => void;
  onAllowContacts: () => void;
  onOpenSettings: () => void;
  onRetryContacts: () => void;
  onRetryCustomers: () => void;
}

interface CustomerPickerListProps extends CustomerPickerListHandlers {
  rows: PickerRow[];
  /** A virtualised list that scrolls on its own; off inside another scroll view. */
  scrollable: boolean;
  /** Room under the last row for a footer laid over the list. */
  bottomPadding?: number;
}

/** The one list: walk in, the order's label, your customers, phone contacts. */
const CustomerPickerList: React.FC<CustomerPickerListProps> = ({
  rows,
  scrollable,
  bottomPadding = 0,
  onSelectWalkIn,
  onSelectLabel,
  onSelectCustomer,
  onSaveCustomer,
  onSelectContact,
  onRetryContact,
  onUseLabel,
  onAddNew,
  onAllowContacts,
  onOpenSettings,
  onRetryContacts,
  onRetryCustomers,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const renderRow = useCallback(
    (row: PickerRow): React.ReactElement | null => {
      switch (row.type) {
        case 'section':
          return (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{row.title}</Text>
              {row.trailing ? (
                <Text style={styles.sectionTrailing}>{row.trailing}</Text>
              ) : null}
            </View>
          );
        case 'walkIn':
          return (
            <PersonRow
              leading={<PersonAvatar name={WALK_IN_CUSTOMER} anonymous />}
              name={WALK_IN_CUSTOMER}
              meta={row.selected ? 'On this order now' : 'No customer details'}
              selected={row.selected}
              onPress={onSelectWalkIn}
            />
          );
        case 'label':
          return (
            <PersonRow
              leading={
                <View style={[styles.tile, styles.tileLabel]}>
                  <Feather
                    name="hash"
                    size={iconSize(18)}
                    color={theme.colors.dueDark}
                  />
                </View>
              }
              name={row.name}
              meta="Order name only · not a saved customer"
              selected={row.selected}
              onPress={() => onSelectLabel(row.name)}
            />
          );
        case 'customer': {
          const saving = row.addToCustomers === 'saving';
          let trailing: React.ReactNode;
          if (saving) {
            trailing = (
              <ActivityIndicator
                size="small"
                color={theme.colors.primaryDark}
              />
            );
          } else if (row.addToCustomers === 'ready') {
            trailing = (
              <Pressable
                onPress={() => onSaveCustomer(row.customer)}
                accessibilityRole="button"
                accessibilityLabel={`Save ${row.customer.name} to customers`}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                style={({ pressed }) => [
                  styles.saveButton,
                  row.selected && styles.saveButtonOnSelected,
                  pressed && styles.pressed,
                ]}>
                <Feather
                  name="user-plus"
                  size={iconSize(17)}
                  color={theme.colors.primaryDark}
                />
              </Pressable>
            );
          }
          return (
            <PersonRow
              leading={
                <PersonAvatar
                  name={row.customer.name}
                  linked={row.customer.isCloud}
                />
              }
              name={row.customer.name}
              highlight={row.highlight}
              meta={saving ? 'Adding to your customers…' : row.meta}
              selected={row.selected}
              disabled={saving}
              trailing={trailing}
              onPress={() => onSelectCustomer(row.customer)}
            />
          );
        }
        case 'contact': {
          const saving = row.state === 'saving';
          const failed = row.state === 'error';
          return (
            <PersonRow
              leading={<PersonAvatar name={row.contact.name} />}
              name={row.contact.name}
              highlight={row.highlight}
              meta={
                saving
                  ? 'Adding to your customers…'
                  : failed
                    ? 'Couldn’t add to customers'
                    : row.contact.phone
              }
              metaTone={failed ? 'error' : 'default'}
              error={failed}
              disabled={saving}
              accessibilityLabel={`${row.contact.name}, phone contact, tap to add to customers`}
              trailing={
                saving ? (
                  <ActivityIndicator
                    size="small"
                    color={theme.colors.primaryDark}
                  />
                ) : failed ? (
                  <Pressable
                    onPress={() => onRetryContact(row.contact)}
                    accessibilityRole="button"
                    accessibilityLabel={`Retry adding ${row.contact.name}`}
                    style={styles.retry}>
                    <Feather
                      name="refresh-cw"
                      size={iconSize(13)}
                      color={theme.colors.errorDarker}
                    />
                    <Text style={styles.retryText}>Retry</Text>
                  </Pressable>
                ) : (
                  <View style={styles.tag}>
                    <Feather
                      name="smartphone"
                      size={iconSize(12)}
                      color={theme.colors.textGray600}
                    />
                    <Text style={styles.tagText}>Contact</Text>
                  </View>
                )
              }
              onPress={() => onSelectContact(row.contact)}
            />
          );
        }
        case 'contactsPermission':
          if (row.status === 'loading') {
            return (
              <View accessibilityLabel="Loading contacts">
                {[0, 1, 2].map(index => (
                  <View key={index} style={styles.skeletonRow}>
                    <View style={styles.skeletonCircle} />
                    <View style={styles.skeletonLines}>
                      <View
                        style={[styles.skeletonLine, styles.skeletonLineWide]}
                      />
                      <View style={styles.skeletonLine} />
                    </View>
                  </View>
                ))}
              </View>
            );
          }
          if (row.status === 'error') {
            return (
              <View style={styles.failRow}>
                <View style={[styles.tile, styles.tileMuted]}>
                  <Feather
                    name="smartphone"
                    size={iconSize(20)}
                    color={theme.colors.textGray500}
                  />
                </View>
                <View style={styles.failText}>
                  <Text style={styles.failTitle}>
                    Couldn’t read your contacts
                  </Text>
                  <Text style={styles.failBody}>
                    Customers and walk in still work
                  </Text>
                </View>
                <Pressable
                  onPress={onRetryContacts}
                  accessibilityRole="button"
                  style={styles.linkButton}>
                  <Text style={styles.linkButtonText}>Retry</Text>
                </Pressable>
              </View>
            );
          }
          if (row.status === 'blocked') {
            return (
              <View style={styles.permissionCard}>
                <View style={[styles.tile, styles.tileMuted]}>
                  <Feather
                    name="smartphone"
                    size={iconSize(22)}
                    color={theme.colors.textGray500}
                  />
                </View>
                <View style={styles.permissionText}>
                  <Text style={styles.permissionTitle}>
                    Contacts access is off
                  </Text>
                  <Text style={styles.permissionBody}>
                    Turn on Contacts for BizPlus in your phone settings to pick
                    customers from your contacts.
                  </Text>
                  <Pressable
                    onPress={onOpenSettings}
                    accessibilityRole="button"
                    accessibilityLabel="Open Settings"
                    style={({ pressed }) => [
                      styles.permissionButton,
                      styles.permissionButtonGhost,
                      pressed && styles.pressed,
                    ]}>
                    <Text style={styles.permissionButtonGhostText}>
                      Open Settings
                    </Text>
                  </Pressable>
                </View>
              </View>
            );
          }
          return (
            <View style={styles.permissionCard}>
              <View style={[styles.tile, styles.tileBlue]}>
                <Feather
                  name="smartphone"
                  size={iconSize(22)}
                  color={theme.colors.primaryDark}
                />
              </View>
              <View style={styles.permissionText}>
                <Text style={styles.permissionTitle}>
                  Sell to people in your contacts
                </Text>
                <Text style={styles.permissionBody}>
                  Tap a contact to save them as a customer and add them to this
                  order. Only names and numbers are read.
                </Text>
                <Pressable
                  onPress={onAllowContacts}
                  accessibilityRole="button"
                  accessibilityLabel="Allow contacts"
                  style={({ pressed }) => [
                    styles.permissionButton,
                    pressed && styles.pressed,
                  ]}>
                  <Text style={styles.permissionButtonText}>
                    Allow contacts
                  </Text>
                </Pressable>
              </View>
            </View>
          );
        case 'contactsEmpty':
          return (
            <Text style={styles.note}>No contacts with phone numbers</Text>
          );
        case 'customersError':
          return (
            <View style={styles.failRow}>
              <View style={[styles.tile, styles.tileMuted]}>
                <Feather
                  name="wifi-off"
                  size={iconSize(20)}
                  color={theme.colors.textGray500}
                />
              </View>
              <View style={styles.failText}>
                <Text style={styles.failTitle}>Couldn’t load customers</Text>
                <Text style={styles.failBody}>
                  Contacts and walk in still work
                </Text>
              </View>
              <Pressable
                onPress={onRetryCustomers}
                accessibilityRole="button"
                style={styles.linkButton}>
                <Text style={styles.linkButtonText}>Retry</Text>
              </Pressable>
            </View>
          );
        case 'customersEmpty':
          return (
            <View style={styles.emptyCard}>
              <View style={styles.emptyIcon}>
                <Feather
                  name="users"
                  size={iconSize(24)}
                  color={theme.colors.primaryDark}
                />
              </View>
              <Text style={styles.emptyTitle}>No customers yet</Text>
              <Pressable
                onPress={() => onAddNew()}
                accessibilityRole="button"
                accessibilityLabel="Add a customer"
                style={({ pressed }) => [
                  styles.noCustomersButton,
                  styles.emptyButton,
                  pressed && styles.pressed,
                ]}>
                <Feather
                  name="user-plus"
                  size={iconSize(15)}
                  color={theme.colors.textInverse}
                />
                <Text style={styles.permissionButtonText}>Add a customer</Text>
              </Pressable>
            </View>
          );
        case 'noMatch':
          return (
            <View style={styles.empty}>
              <View style={[styles.emptyIcon]}>
                <Feather
                  name="search"
                  size={iconSize(26)}
                  color={theme.colors.textGray500}
                />
              </View>
              <Text style={styles.emptyTitle}>
                No customer or contact named “{row.query}”
              </Text>
              <Text style={styles.emptyBody}>
                Use it as the order name, or save it as a new customer.
              </Text>
            </View>
          );
        case 'useLabel':
          return (
            <ActionCard
              styles={styles}
              icon={
                <View style={[styles.tile, styles.tileLabel]}>
                  <Feather
                    name="hash"
                    size={iconSize(20)}
                    color={theme.colors.dueDark}
                  />
                </View>
              }
              title={`Use “${row.name}” for this order`}
              body="Order name only · not saved to customers"
              chevronColor={theme.colors.textPlaceholder}
              onPress={() => onUseLabel(row.name)}
            />
          );
        case 'addNew':
          return (
            <ActionCard
              styles={styles}
              icon={
                <View style={[styles.tile, styles.tileBlue]}>
                  <Feather
                    name="user-plus"
                    size={iconSize(20)}
                    color={theme.colors.primaryDark}
                  />
                </View>
              }
              title={`Add “${row.name}” as a new customer`}
              body="Saved to Back Office › Customers"
              chevronColor={theme.colors.textPlaceholder}
              onPress={() => onAddNew(row.name)}
            />
          );
        case 'addNewLink':
          return (
            <View>
              <View style={styles.hairline} />
              <PersonRow
                leading={
                  <View style={[styles.avatarTile, styles.tileBlue]}>
                    <Feather
                      name="user-plus"
                      size={iconSize(18)}
                      color={theme.colors.primaryDark}
                    />
                  </View>
                }
                name="Add new customer"
                meta="Not listed? Save their details"
                selected={false}
                onPress={() => onAddNew()}
              />
            </View>
          );
        case 'more':
          return (
            <Text style={styles.note}>
              {row.hidden} more {row.what} · search to find them
            </Text>
          );
        default:
          return null;
      }
    },
    [
      styles,
      theme,
      onSelectWalkIn,
      onSelectLabel,
      onSelectCustomer,
      onSaveCustomer,
      onSelectContact,
      onRetryContact,
      onUseLabel,
      onAddNew,
      onAllowContacts,
      onOpenSettings,
      onRetryContacts,
      onRetryCustomers,
    ],
  );

  const keyExtractor = useCallback((row: PickerRow) => row.key, []);
  const renderItem = useCallback(
    ({ item }: { item: PickerRow }) => renderRow(item),
    [renderRow],
  );

  if (!scrollable) {
    return (
      <View style={styles.staticList}>
        {rows.map(row => (
          <React.Fragment key={row.key}>{renderRow(row)}</React.Fragment>
        ))}
      </View>
    );
  }

  return (
    <BottomSheetFlatList
      data={rows}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      contentContainerStyle={[
        styles.listContent,
        { paddingBottom: bottomPadding + v(12) },
      ]}
      initialNumToRender={14}
      windowSize={7}
      removeClippedSubviews={Platform.OS !== 'web'}
      showsVerticalScrollIndicator={false}
    />
  );
};

const ActionCard: React.FC<{
  styles: ReturnType<typeof createStyles>;
  icon: React.ReactNode;
  title: string;
  body: string;
  chevronColor: string;
  onPress: () => void;
}> = ({ styles, icon, title, body, chevronColor, onPress }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={title}
    style={({ pressed }) => [styles.actionCard, pressed && styles.pressed]}>
    {icon}
    <View style={styles.actionText}>
      <Text style={styles.actionTitle} numberOfLines={2}>
        {title}
      </Text>
      <Text style={styles.actionBody} numberOfLines={1}>
        {body}
      </Text>
    </View>
    <Feather name="chevron-right" size={iconSize(18)} color={chevronColor} />
  </Pressable>
);

const createStyles = (theme: any) =>
  StyleSheet.create({
    // The gap under the search bar matches the one above it.
    listContent: {
      paddingTop: v(8),
      paddingHorizontal: s(8),
    },
    staticList: {
      marginHorizontal: -s(4),
    },
    section: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: s(8),
      paddingTop: v(14),
      paddingBottom: v(4),
      paddingHorizontal: s(12),
    },
    sectionTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(11),
      lineHeight: fs(14),
      letterSpacing: 0.7,
      textTransform: 'uppercase',
      color: theme.colors.textGray500,
    },
    sectionTrailing: {
      flexShrink: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(14),
      color: theme.colors.textGray500,
    },
    tile: {
      width: s(42),
      height: s(42),
      borderRadius: s(12),
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarTile: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
    },
    tileLabel: {
      backgroundColor: theme.colors.warningLight,
    },
    tileBlue: {
      backgroundColor: theme.colors.primarySurface,
    },
    tileMuted: {
      backgroundColor: theme.colors.surfaceSecondary,
    },
    saveButton: {
      width: s(34),
      height: s(34),
      borderRadius: s(10),
      backgroundColor: theme.colors.primarySurface,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    // The selected row is tinted the button's colour.
    saveButtonOnSelected: {
      backgroundColor: theme.colors.surface,
    },
    tag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(5),
      paddingHorizontal: s(8),
      paddingVertical: v(4),
      borderRadius: s(8),
      backgroundColor: theme.colors.surfaceSecondary,
    },
    tagText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(15),
      color: theme.colors.textGray600,
    },
    retry: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      height: v(34),
      paddingHorizontal: s(12),
      borderRadius: s(10),
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.errorLight,
      backgroundColor: theme.colors.surface,
    },
    retryText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.errorDarker,
    },
    permissionCard: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s(12),
      marginHorizontal: s(4),
      marginTop: v(4),
      padding: s(14),
      borderRadius: s(16),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.backgroundGray50,
    },
    permissionText: {
      flex: 1,
      minWidth: 0,
      gap: v(4),
    },
    permissionTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    permissionBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray600,
    },
    permissionButton: {
      alignSelf: 'flex-start',
      marginTop: v(8),
      height: v(38),
      paddingHorizontal: s(16),
      borderRadius: s(10),
      backgroundColor: theme.colors.primaryDark,
      alignItems: 'center',
      justifyContent: 'center',
    },
    permissionButtonText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textInverse,
    },
    permissionButtonGhost: {
      backgroundColor: theme.colors.surface,
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.borderGray200,
    },
    permissionButtonGhostText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    skeletonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      minHeight: v(60),
      paddingHorizontal: s(12),
    },
    skeletonCircle: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      backgroundColor: theme.colors.border,
    },
    skeletonLines: {
      flex: 1,
      gap: v(8),
    },
    skeletonLine: {
      height: v(10),
      width: '40%',
      borderRadius: s(5),
      backgroundColor: theme.colors.border,
    },
    skeletonLineWide: {
      width: '60%',
    },
    failRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      paddingVertical: v(10),
      paddingHorizontal: s(12),
    },
    failText: {
      flex: 1,
      minWidth: 0,
      gap: v(1),
    },
    failTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    failBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textGray500,
    },
    linkButton: {
      height: v(34),
      paddingHorizontal: s(12),
      borderRadius: s(10),
      backgroundColor: theme.colors.primarySurface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    linkButtonText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.primaryDark,
    },
    note: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray500,
      textAlign: 'center',
      paddingVertical: v(12),
      paddingHorizontal: s(12),
    },
    empty: {
      alignItems: 'center',
      paddingTop: v(30),
      paddingBottom: v(20),
      paddingHorizontal: s(16),
    },
    emptyIcon: {
      width: s(56),
      height: s(56),
      borderRadius: s(18),
      backgroundColor: theme.colors.surfaceSecondary,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: v(14),
    },
    emptyCard: {
      alignItems: 'center',
      marginHorizontal: s(4),
      marginTop: v(4),
      paddingTop: v(22),
      paddingBottom: v(18),
      paddingHorizontal: s(20),
      borderRadius: s(16),
      borderWidth: borderWidth(1),
      borderStyle: 'dashed',
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.backgroundGray50,
    },
    emptyButton: {
      alignSelf: 'center',
      flexDirection: 'row',
      gap: s(8),
      marginTop: v(14),
      paddingHorizontal: s(18),
    },
    emptyTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(22),
      color: theme.colors.text,
      textAlign: 'center',
    },
    noCustomersButton: {
      marginTop: v(8),
      height: v(40),
      paddingHorizontal: s(16),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(19),
      color: theme.colors.textGray500,
      textAlign: 'center',
      marginTop: v(4),
    },
    actionCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(14),
      marginHorizontal: s(4),
      marginBottom: v(10),
      padding: s(14),
      borderRadius: s(16),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    actionText: {
      flex: 1,
      minWidth: 0,
      gap: v(2),
    },
    actionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    actionBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textGray500,
    },
    hairline: {
      height: borderWidth(1),
      backgroundColor: theme.colors.border,
      marginVertical: v(8),
      marginHorizontal: s(12),
    },
    pressed: {
      opacity: 0.75,
    },
  });

export default CustomerPickerList;
